// src/pages/EarlyAccessLeadPage.jsx
import React, { useMemo, useState } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db as defaultDb } from "../firebase";

const BRAND = "#00675f";
const BRAND_2 = "#00897f";

const COUNTRY_CODES = [
  { code: "+507", country: "PA", label: "Panamá", flag: "🇵🇦", placeholder: "6000-0000" },
  { code: "+1", country: "US", label: "Estados Unidos", flag: "🇺🇸", placeholder: "555 000 0000" },
  { code: "+57", country: "CO", label: "Colombia", flag: "🇨🇴", placeholder: "300 000 0000" },
  { code: "+506", country: "CR", label: "Costa Rica", flag: "🇨🇷", placeholder: "8888-8888" },
  { code: "+52", country: "MX", label: "México", flag: "🇲🇽", placeholder: "55 0000 0000" },
  { code: "+34", country: "ES", label: "España", flag: "🇪🇸", placeholder: "600 000 000" },
  { code: "+55", country: "BR", label: "Brasil", flag: "🇧🇷", placeholder: "11 99999-9999" },
  { code: "+54", country: "AR", label: "Argentina", flag: "🇦🇷", placeholder: "11 0000-0000" },
  { code: "+56", country: "CL", label: "Chile", flag: "🇨🇱", placeholder: "9 0000 0000" },
  { code: "+51", country: "PE", label: "Perú", flag: "🇵🇪", placeholder: "900 000 000" },
];

function cleanPhone(value) {
  return String(value || "")
    .replace(/[^\d\s-]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function friendlyLeadError(error) {
  const raw = String(error?.code || error?.message || error || "").toLowerCase();

  if (raw.includes("permission-denied")) {
    return "No pudimos guardar tu solicitud por permisos. Revisa las reglas de Firestore para earlyAccessLeads.";
  }

  if (raw.includes("network")) {
    return "No pudimos conectarnos. Revisa tu internet e intenta nuevamente.";
  }

  return "No pudimos enviar tu solicitud. Intenta nuevamente en unos minutos.";
}

export default function EarlyAccessLeadPage({ db = defaultDb }) {
  const [name, setName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [countryCode, setCountryCode] = useState("+507");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [message, setMessage] = useState("");

  const [fieldErrors, setFieldErrors] = useState({});
  const [status, setStatus] = useState({ type: "", message: "" });
  const [saving, setSaving] = useState(false);

  const selectedCountry = useMemo(
    () => COUNTRY_CODES.find((item) => item.code === countryCode) || COUNTRY_CODES[0],
    [countryCode]
  );

  const validate = () => {
    const next = {};
    const emailTrim = String(email || "").trim().toLowerCase();
    const phoneLocal = cleanPhone(phone);

    if (!String(name || "").trim()) next.name = "Ingresa tu nombre.";
    if (!String(businessName || "").trim()) next.businessName = "Ingresa el nombre de tu negocio.";
    if (!phoneLocal) next.phone = "Ingresa tu número de teléfono.";
    if (!emailTrim) next.email = "Ingresa tu correo.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) next.email = "Correo inválido.";

    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    setStatus({ type: "", message: "" });
    setFieldErrors({});

    if (!validate()) return;

    setSaving(true);

    const phoneLocal = cleanPhone(phone);
    const fullPhone = `${countryCode} ${phoneLocal}`.trim();
    const emailTrim = String(email || "").trim().toLowerCase();

    try {
      await addDoc(collection(db, "earlyAccessLeads"), {
        name: String(name || "").trim(),
        businessName: String(businessName || "").trim(),
        phone: fullPhone,
        phoneCountryCode: countryCode,
        phoneLocal,
        email: emailTrim,
        businessType: String(businessType || "").trim(),
        message: String(message || "").trim(),
        source: "mainmenu_early_access",
        status: "new",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setStatus({
        type: "success",
        message: "Listo. Recibimos tu solicitud de early access. Te contactaremos pronto.",
      });

      setName("");
      setBusinessName("");
      setPhone("");
      setEmail("");
      setBusinessType("");
      setMessage("");
    } catch (error) {
      console.error("[EarlyAccessLeadPage] submit failed", error);
      setStatus({ type: "error", message: friendlyLeadError(error) });
    } finally {
      setSaving(false);
    }
  };

  const inputClass = (key) => `early-access-input ${fieldErrors[key] ? "is-error" : ""}`;

  return (
    <main className="early-access-page">
      <style>{styles}</style>

      <section className="early-access-shell">
        <div className="early-access-copy">
          <div className="early-access-pill">
            <span />
            Early access · Panamá
          </div>

          <h1>Únete al lanzamiento privado de Loop.</h1>

          <p>
            Estamos invitando a los primeros negocios de belleza, bienestar y servicios locales
            a formar parte de la etapa inicial de Loop.
          </p>

          <div className="early-access-benefits">
            <div>
              <strong>Perfil digital</strong>
              <span>Tu negocio listo para recibir clientes.</span>
            </div>
            <div>
              <strong>Reservas simples</strong>
              <span>Horarios, servicios y agenda en una sola plataforma.</span>
            </div>
            <div>
              <strong>Acceso curado</strong>
              <span>Cupos limitados para los primeros negocios.</span>
            </div>
          </div>
        </div>

        <form className="early-access-card" onSubmit={handleSubmit}>
          <div className="early-access-card-head">
            <div className="early-access-logo">∞</div>
            <div>
              <div className="early-access-eyebrow">Solicitud de acceso</div>
              <h2>Cuéntanos de tu negocio</h2>
              <p>No necesitas crear cuenta todavía.</p>
            </div>
          </div>

          {status.message ? (
            <div className={`early-access-alert ${status.type === "success" ? "is-success" : "is-error"}`}>
              {status.message}
            </div>
          ) : null}

          <div className="early-access-grid">
            <label className="wide">
              <span>Tu nombre *</span>
              <input
                className={inputClass("name")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nombre y apellido"
                autoComplete="name"
              />
              {fieldErrors.name ? <small>{fieldErrors.name}</small> : null}
            </label>

            <label className="wide">
              <span>Nombre de la empresa *</span>
              <input
                className={inputClass("businessName")}
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Ej. Faces Beauty Club"
                autoComplete="organization"
              />
              {fieldErrors.businessName ? <small>{fieldErrors.businessName}</small> : null}
            </label>

            <label>
              <span>País</span>
              <select
                className="early-access-input"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
              >
                {COUNTRY_CODES.map((item) => (
                  <option key={`${item.country}-${item.code}`} value={item.code}>
                    {item.flag} {item.code} · {item.label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Teléfono *</span>
              <input
                className={inputClass("phone")}
                value={phone}
                onChange={(e) => setPhone(cleanPhone(e.target.value))}
                type="tel"
                placeholder={selectedCountry.placeholder}
                autoComplete="tel-national"
              />
              {fieldErrors.phone ? <small>{fieldErrors.phone}</small> : null}
            </label>

            <label className="wide">
              <span>Correo *</span>
              <input
                className={inputClass("email")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                placeholder="correo@empresa.com"
                autoComplete="email"
              />
              {fieldErrors.email ? <small>{fieldErrors.email}</small> : null}
            </label>

            <label className="wide">
              <span>Tipo de negocio</span>
              <select
                className="early-access-input"
                value={businessType}
                onChange={(e) => setBusinessType(e.target.value)}
              >
                <option value="">Selecciona una opción</option>
                <option value="beauty">Belleza & Barberías</option>
                <option value="wellness">Bienestar & Spa</option>
                <option value="events">Eventos</option>
                <option value="fitness">Entretenimiento & Fitness</option>
                <option value="others">Otros</option>
              </select>
            </label>

            <label className="wide">
              <span>Mensaje opcional</span>
              <textarea
                className="early-access-input early-access-textarea"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Cuéntanos qué servicios ofreces o qué necesitas de Loop."
                rows={4}
              />
            </label>
          </div>

          <button type="submit" className="early-access-submit" disabled={saving}>
            {saving ? "Enviando..." : "Solicitar early access"}
          </button>

          <p className="early-access-footnote">
            Te contactaremos para validar tu negocio y ayudarte con los primeros pasos.
          </p>
        </form>
      </section>
    </main>
  );
}

const styles = `
  .early-access-page {
    min-height: calc(100dvh - 86px);
    padding: clamp(28px, 5vw, 76px) 18px calc(38px + env(safe-area-inset-bottom, 0px));
    background:
      radial-gradient(circle at top left, rgba(0,103,95,0.12), transparent 30%),
      radial-gradient(circle at bottom right, rgba(20,184,166,0.10), transparent 30%),
      linear-gradient(180deg, #f8fafc 0%, #ffffff 55%, #f8fafc 100%);
    box-sizing: border-box;
    color: #0f172a;
  }

  .early-access-shell {
    width: min(100%, 1180px);
    margin: 0 auto;
    display: grid;
    grid-template-columns: minmax(0, 0.95fr) minmax(420px, 0.78fr);
    gap: clamp(28px, 6vw, 82px);
    align-items: center;
  }

  .early-access-copy {
    min-width: 0;
  }

  .early-access-pill {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    width: fit-content;
    padding: 8px 14px;
    border: 1px solid rgba(0,103,95,0.14);
    background: rgba(255,255,255,0.78);
    color: ${BRAND};
    border-radius: 999px;
    font-size: 12px;
    font-weight: 950;
    letter-spacing: 0.11em;
    text-transform: uppercase;
    box-shadow: 0 10px 24px rgba(15,23,42,0.055);
  }

  .early-access-pill span {
    width: 9px;
    height: 9px;
    border-radius: 999px;
    background: ${BRAND};
    box-shadow: 0 0 0 6px rgba(0,103,95,0.08);
  }

  .early-access-copy h1 {
    margin: 28px 0 0;
    color: #07111f;
    font-size: clamp(44px, 6vw, 78px);
    line-height: 0.94;
    letter-spacing: -0.078em;
    font-weight: 950;
  }

  .early-access-copy p {
    max-width: 620px;
    margin: 24px 0 0;
    color: #667085;
    font-size: clamp(17px, 1.3vw, 20px);
    line-height: 1.55;
    font-weight: 600;
  }

  .early-access-benefits {
    margin-top: 34px;
    display: grid;
    gap: 13px;
    max-width: 620px;
  }

  .early-access-benefits div {
    border: 1px solid rgba(226,232,240,0.9);
    background: rgba(255,255,255,0.76);
    border-radius: 22px;
    padding: 15px 16px;
    box-shadow: 0 12px 30px rgba(15,23,42,0.055);
  }

  .early-access-benefits strong {
    display: block;
    color: #0f172a;
    font-size: 14px;
    font-weight: 950;
    margin-bottom: 4px;
  }

  .early-access-benefits span {
    display: block;
    color: #64748b;
    font-size: 13px;
    line-height: 1.35;
    font-weight: 700;
  }

  .early-access-card {
    background: rgba(255,255,255,0.98);
    border: 1px solid rgba(226,232,240,0.96);
    border-radius: 34px;
    box-shadow: 0 28px 90px rgba(15,23,42,0.13);
    padding: 26px;
    box-sizing: border-box;
  }

  .early-access-card-head {
    display: flex;
    gap: 15px;
    align-items: flex-start;
    margin-bottom: 18px;
  }

  .early-access-logo {
    width: 58px;
    height: 58px;
    border-radius: 22px;
    background: linear-gradient(135deg, ${BRAND}, ${BRAND_2});
    color: #ffffff;
    display: grid;
    place-items: center;
    font-size: 30px;
    font-weight: 950;
    box-shadow: 0 16px 34px rgba(0,103,95,0.22);
    flex: 0 0 auto;
  }

  .early-access-eyebrow {
    color: ${BRAND};
    font-size: 11px;
    font-weight: 950;
    letter-spacing: 0.13em;
    text-transform: uppercase;
    margin-bottom: 6px;
  }

  .early-access-card h2 {
    margin: 0;
    color: #0f172a;
    font-size: clamp(28px, 4vw, 40px);
    line-height: 0.98;
    letter-spacing: -0.055em;
    font-weight: 950;
  }

  .early-access-card-head p {
    margin: 8px 0 0;
    color: #64748b;
    font-size: 13px;
    line-height: 1.4;
    font-weight: 750;
  }

  .early-access-alert {
    border-radius: 20px;
    padding: 13px 14px;
    font-size: 13px;
    font-weight: 850;
    margin-bottom: 16px;
  }

  .early-access-alert.is-success {
    border: 1px solid #bbf7d0;
    background: #ecfdf5;
    color: #047857;
  }

  .early-access-alert.is-error {
    border: 1px solid #fecaca;
    background: #fef2f2;
    color: #b91c1c;
  }

  .early-access-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 13px;
  }

  .early-access-grid label {
    min-width: 0;
  }

  .early-access-grid label.wide {
    grid-column: 1 / -1;
  }

  .early-access-grid label > span {
    display: block;
    color: #334155;
    font-size: 13px;
    font-weight: 900;
    margin-bottom: 7px;
  }

  .early-access-input {
    width: 100%;
    min-height: 52px;
    border-radius: 18px;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    color: #0f172a;
    outline: none;
    font-size: 15px;
    font-weight: 760;
    padding: 0 14px;
    box-sizing: border-box;
    transition: border-color 150ms ease, box-shadow 150ms ease, background 150ms ease;
  }

  .early-access-input:focus {
    border-color: rgba(0,103,95,0.42);
    box-shadow: 0 0 0 4px rgba(0,103,95,0.10);
    background: #fcfffe;
  }

  .early-access-input.is-error {
    border-color: #fecaca;
    background: #fff7f7;
  }

  .early-access-textarea {
    min-height: 112px;
    padding-top: 14px;
    resize: vertical;
    line-height: 1.45;
  }

  .early-access-grid small {
    display: block;
    margin-top: 6px;
    color: #dc2626;
    font-size: 12px;
    font-weight: 800;
  }

  .early-access-submit {
    width: 100%;
    min-height: 54px;
    margin-top: 16px;
    border: 0;
    border-radius: 20px;
    background: linear-gradient(135deg, ${BRAND}, ${BRAND_2});
    color: #ffffff;
    font-size: 15px;
    font-weight: 950;
    box-shadow: 0 16px 34px rgba(0,103,95,0.23);
    cursor: pointer;
  }

  .early-access-submit:disabled {
    opacity: 0.62;
    cursor: not-allowed;
  }

  .early-access-footnote {
    margin: 12px 0 0;
    text-align: center;
    color: #64748b;
    font-size: 12px;
    line-height: 1.45;
    font-weight: 750;
  }

  @media (max-width: 920px) {
    .early-access-shell {
      grid-template-columns: 1fr;
      gap: 24px;
    }

    .early-access-copy h1 {
      max-width: 720px;
    }

    .early-access-benefits {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 560px) {
    .early-access-page {
      padding: 18px 12px calc(24px + env(safe-area-inset-bottom, 0px));
    }

    .early-access-copy h1 {
      font-size: 42px;
      letter-spacing: -0.068em;
    }

    .early-access-copy p {
      font-size: 15px;
      margin-top: 16px;
    }

    .early-access-benefits {
      margin-top: 20px;
    }

    .early-access-card {
      padding: 20px;
      border-radius: 28px;
    }

    .early-access-card-head {
      gap: 12px;
    }

    .early-access-logo {
      width: 50px;
      height: 50px;
      border-radius: 19px;
      font-size: 27px;
    }

    .early-access-grid {
      grid-template-columns: 1fr;
    }

    .early-access-grid label.wide {
      grid-column: auto;
    }
  }

  /* =========================================================
     MOBILE-FIRST EARLY ACCESS REFRESH
     QR traffic: compact hero, form visible faster, easier fields.
     ========================================================= */

  @media (max-width: 920px) {
    .early-access-page {
      min-height: 100dvh;
      padding: 14px 12px calc(22px + env(safe-area-inset-bottom, 0px));
      background:
        radial-gradient(circle at 12% 0%, rgba(0,103,95,0.12), transparent 28%),
        linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    }

    .early-access-shell {
      width: 100%;
      max-width: 560px;
      display: block;
      margin: 0 auto;
    }

    .early-access-copy {
      text-align: left;
      margin-bottom: 12px;
    }

    .early-access-pill {
      padding: 6px 11px;
      font-size: 10px;
      letter-spacing: 0.10em;
      box-shadow: none;
      background: #ecfdf5;
    }

    .early-access-pill span {
      width: 7px;
      height: 7px;
      box-shadow: 0 0 0 4px rgba(0,103,95,0.08);
    }

    .early-access-copy h1 {
      margin-top: 13px;
      max-width: 390px;
      font-size: clamp(30px, 8.7vw, 38px);
      line-height: 0.96;
      letter-spacing: -0.062em;
    }

    .early-access-copy p {
      max-width: 430px;
      margin-top: 10px;
      font-size: 13.5px;
      line-height: 1.42;
      font-weight: 650;
    }

    .early-access-benefits {
      margin-top: 12px;
      display: flex;
      gap: 8px;
      overflow-x: auto;
      padding: 2px 2px 6px;
      scrollbar-width: none;
      -webkit-overflow-scrolling: touch;
    }

    .early-access-benefits::-webkit-scrollbar {
      display: none;
    }

    .early-access-benefits div {
      flex: 0 0 152px;
      border-radius: 18px;
      padding: 11px 12px;
      box-shadow: 0 8px 22px rgba(15,23,42,0.045);
      background: rgba(255,255,255,0.88);
    }

    .early-access-benefits strong {
      font-size: 12.5px;
      margin-bottom: 3px;
    }

    .early-access-benefits span {
      font-size: 11.5px;
      line-height: 1.25;
    }

    .early-access-card {
      border-radius: 26px;
      padding: 16px;
      box-shadow: 0 18px 46px rgba(15,23,42,0.10);
    }

    .early-access-card-head {
      margin-bottom: 14px;
      gap: 11px;
      align-items: center;
    }

    .early-access-logo {
      width: 44px;
      height: 44px;
      border-radius: 16px;
      font-size: 24px;
      box-shadow: 0 10px 24px rgba(0,103,95,0.18);
    }

    .early-access-eyebrow {
      font-size: 9.5px;
      letter-spacing: 0.11em;
      margin-bottom: 4px;
    }

    .early-access-card h2 {
      font-size: clamp(24px, 6.4vw, 30px);
      line-height: 1;
      letter-spacing: -0.052em;
    }

    .early-access-card-head p {
      margin-top: 5px;
      font-size: 12px;
      line-height: 1.25;
    }

    .early-access-grid {
      grid-template-columns: 1fr;
      gap: 10px;
    }

    .early-access-grid label.wide {
      grid-column: auto;
    }

    .early-access-grid label > span {
      font-size: 12px;
      margin-bottom: 5px;
    }

    .early-access-input {
      min-height: 46px;
      border-radius: 16px;
      font-size: 14px;
      padding: 0 12px;
    }

    .early-access-textarea {
      min-height: 82px;
      padding-top: 11px;
    }

    .early-access-submit {
      min-height: 50px;
      margin-top: 13px;
      border-radius: 18px;
      font-size: 14.5px;
      position: sticky;
      bottom: calc(10px + env(safe-area-inset-bottom, 0px));
      z-index: 5;
    }

    .early-access-footnote {
      font-size: 11.5px;
      margin-top: 10px;
      padding-bottom: 2px;
    }
  }

  @media (max-width: 390px) {
    .early-access-page {
      padding-left: 10px;
      padding-right: 10px;
    }

    .early-access-copy h1 {
      font-size: 29px;
    }

    .early-access-copy p {
      font-size: 13px;
    }

    .early-access-benefits div {
      flex-basis: 142px;
    }

    .early-access-card {
      padding: 14px;
      border-radius: 24px;
    }
  }

`;
